import { onMount, reactive, Icon } from 'cdui-js';
import { showActionSheet } from '../ActionSheet';
import i18n from '../../../../i18n';
import './style.css';

interface Props {
  value?: string;
  change?: (val: string) => void;
  placeholder?: string;
  title?: string;
  /** 年份下限，默认 1900 */
  minYear?: number;
  /** 年份上限，默认当前年份 */
  maxYear?: number;
}

const THIS_YEAR = new Date().getFullYear();
const MONTHS = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
const DAYS_31 = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));
const ITEM_H = 44;

const buildYears = (min: number, max: number) => Array.from({ length: max - min + 1 }, (_, i) => String(min + i));

const daysInMonth = (year: string, month: string) => new Date(+year, +month, 0).getDate();

const clampDay = (year: string, month: string, day: string): string => {
  const max = daysInMonth(year, month);
  return String(Math.min(Math.max(1, +day), max)).padStart(2, '0');
};

const parseValue = (v: string, minYear: number, maxYear: number): [string, string, string] => {
  if (v) {
    const p = v.split('-');
    if (p.length === 3 && p[0].length === 4) {
      return [p[0], p[1], p[2]];
    }
  }
  // 默认落在范围内最近的合理年份
  const defaultYear = Math.min(Math.max(THIS_YEAR - 18, minYear), maxYear);
  const d = new Date();
  return [String(defaultYear), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')];
};

const DatePickerPanel = (panelProps: {
  value: string;
  minYear: number;
  maxYear: number;
  onConfirm: (v: string) => void;
  close: () => void;
}) => {
  const YEARS = buildYears(panelProps.minYear, panelProps.maxYear);
  const [iy, im, id] = parseValue(panelProps.value, panelProps.minYear, panelProps.maxYear);

  // 保证初始年份在合法范围内
  const initYear = YEARS.includes(iy) ? iy : YEARS[YEARS.length - 1];
  const state = reactive({ year: initYear, month: im, day: id });

  let yearEl: HTMLDivElement;
  let monthEl: HTMLDivElement;
  let dayEl: HTMLDivElement;

  const snapTo = (el: HTMLDivElement, idx: number, smooth = false) => {
    el?.scrollTo({ top: idx * ITEM_H, behavior: smooth ? 'smooth' : ('instant' as any) });
  };

  const attachSnap = (el: HTMLDivElement, items: string[], onSelect: (v: string) => void) => {
    let t: ReturnType<typeof setTimeout>;
    el.addEventListener('scroll', () => {
      clearTimeout(t);
      t = setTimeout(() => {
        const idx = Math.max(0, Math.min(Math.round(el.scrollTop / ITEM_H), items.length - 1));
        if (Math.abs(el.scrollTop - idx * ITEM_H) > 1) snapTo(el, idx, true);
        onSelect(items[idx]);
      }, 120);
    });
  };

  onMount(() => {
    const yIdx = Math.max(0, YEARS.indexOf(state.year));
    const mIdx = Math.max(0, MONTHS.indexOf(state.month));
    const dIdx = Math.max(0, DAYS_31.indexOf(state.day));

    snapTo(yearEl, yIdx);
    snapTo(monthEl, mIdx);
    snapTo(dayEl, dIdx);

    attachSnap(yearEl, YEARS, (v) => {
      state.year = v;
      const max = daysInMonth(v, state.month);
      if (+state.day > max) {
        state.day = String(max).padStart(2, '0');
        snapTo(dayEl, max - 1, true);
      }
    });

    attachSnap(monthEl, MONTHS, (v) => {
      state.month = v;
      const max = daysInMonth(state.year, v);
      if (+state.day > max) {
        state.day = String(max).padStart(2, '0');
        snapTo(dayEl, max - 1, true);
      }
    });

    attachSnap(dayEl, DAYS_31, (v) => {
      state.day = v;
    });
  });

  const maxDay = () => daysInMonth(state.year, state.month);

  return (
    <div>
      <div class="dp-wrap">
        <div class="dp-mask dp-mask-top" />
        <div class="dp-mask dp-mask-bottom" />
        <div class="dp-line dp-line-top" />
        <div class="dp-line dp-line-bottom" />

        <div ref={(el) => (yearEl = el)} class="dp-col">
          <div class="dp-filler" />
          {YEARS.map((y) => (
            <div class="dp-item">{y}</div>
          ))}
          <div class="dp-filler" />
        </div>

        <div ref={(el) => (monthEl = el)} class="dp-col">
          <div class="dp-filler" />
          {MONTHS.map((m) => (
            <div class="dp-item">{m}</div>
          ))}
          <div class="dp-filler" />
        </div>

        <div ref={(el) => (dayEl = el)} class="dp-col">
          <div class="dp-filler" />
          {DAYS_31.map((d) => (
            <div class={`dp-item${+d > maxDay() ? ' dp-item-disabled' : ''}`}>{+d <= maxDay() ? d : ''}</div>
          ))}
          <div class="dp-filler" />
        </div>
      </div>

      <div class="dp-actions">
        <div class="dp-btn dp-btn-cancel" onclick={panelProps.close}>
          {i18n.Common.Cancel}
        </div>
        <div
          class="dp-btn dp-btn-confirm"
          onclick={() => {
            const safeDay = clampDay(state.year, state.month, state.day);
            panelProps.onConfirm(`${state.year}-${state.month}-${safeDay}`);
            panelProps.close();
          }}
        >
          {i18n.Common.Confirm}
        </div>
      </div>
    </div>
  );
};

export const DatePicker = (props: Props) => {
  const state = reactive({ dialog: null as any });

  const minYear = () => props.minYear ?? 1900;
  const maxYear = () => props.maxYear ?? THIS_YEAR;

  const handleClick = () => {
    state.dialog = showActionSheet({
      title: props.title || i18n.Common.SelectDate,
      content: () => (
        <DatePickerPanel
          value={props.value || ''}
          minYear={minYear()}
          maxYear={maxYear()}
          onConfirm={(v) => props.change?.(v)}
          close={() => state.dialog?.close()}
        />
      ),
    });
  };

  return (
    <div class="dp-trigger" onclick={handleClick}>
      {props.value ? (
        <div class="dp-trigger-value">{props.value}</div>
      ) : (
        <div class="dp-trigger-placeholder">{props.placeholder || i18n.Common.PleaseSelect}</div>
      )}
      {/* <Icon name="common-arrow-down" class="icon-s-S icon-c-D" /> */}
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M15.8053 3.4375V7.24306M8.19423 3.4375V7.24306M4.38867 11.0486H19.6109M7.24284 13.9028H7.25521M10.1065 13.9028H10.1113M12.9607 13.9028H12.9654M15.8196 13.9028H15.8244M12.9654 16.7569H12.9702M7.25235 16.7569H7.25711M10.1065 16.7569H10.1113M4.38867 5.34028H19.6109V20.5625H4.38867V5.34028Z"
          stroke="#A2A7AD"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  );
};
